import React, { useEffect, useState, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, formatApiError } from "../lib/api";
import { setFaviconBadge, clearFaviconBadge } from "../lib/favicon";
import { toast } from "sonner";
import RegistrationDialog from "../components/RegistrationDialog";
import { CheckSquare, Square, Lightning, ArrowsClockwise, X, MagnifyingGlass, CaretLeft, CaretRight } from "@phosphor-icons/react";

const POLL_MS = 25_000; // background refresh cadence
const PAGE_SIZE = 25;

export default function PendingApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pendingNew, setPendingNew] = useState(0); // count of fresh items waiting to be merged
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0); // zero-based
  const [total, setTotal] = useState(0);
  const knownIdsRef = useRef(new Set());

  // Debounce search input by 250ms — typing won't spam the API
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: "pending",
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      };
      if (debounced) params.q = debounced;
      const [list, count] = await Promise.all([
        api.get("/registrations", { params }),
        api.get("/registrations/count", { params: { status: "pending", q: debounced || undefined } }),
      ]);
      setItems(list.data);
      setTotal(count.data?.total || 0);
      setPicked(new Set());
      knownIdsRef.current = new Set(list.data.map((r) => r.id));
      setPendingNew(0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [page, debounced]);

  useEffect(() => {
    load();
  }, [load]);

  // Background poll — counts new pending IDs without disturbing the visible list.
  // The user reloads explicitly so the queue order / selection / scroll position
  // never shifts while they're working.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/registrations", { params: { status: "pending" } });
        if (cancelled) return;
        const known = knownIdsRef.current;
        if (known.size === 0) return; // initial load hasn't finished
        const fresh = data.filter((r) => !known.has(r.id)).length;
        if (fresh > 0) setPendingNew(fresh);
      } catch {
        // Silent — keep the current list visible if the network blips
      }
    };
    const id = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Mirror the new-application count onto the favicon so admins see it in the
  // tab bar even when this page is in the background.
  useEffect(() => {
    setFaviconBadge(pendingNew);
    return () => clearFaviconBadge();
  }, [pendingNew]);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (picked.size === items.length) setPicked(new Set());
    else setPicked(new Set(items.map((r) => r.id)));
  };

  const bulkApprove = async () => {
    if (picked.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/registrations/bulk-approve", { ids: Array.from(picked) });
      toast.success(`Approved ${data.approved}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Bulk approve failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-warning)] mb-3">Queue</div>
          <h1 className="font-display text-5xl font-black tracking-tighter">Pending approvals.</h1>
          <p className="text-slate-500 mt-2">
            {total} applicant{total === 1 ? "" : "s"} awaiting review
            {debounced && <span className="font-mono-sms text-xs ml-2 text-[var(--sms-primary)]">· filtered "{debounced}"</span>}
          </p>
        </div>
        {picked.size > 0 && (
          <button
            onClick={bulkApprove}
            disabled={bulkBusy}
            data-testid="btn-bulk-approve"
            className="sms-btn-primary inline-flex items-center gap-2 sms-fade-up"
          >
            <Lightning size={16} weight="bold" />
            {bulkBusy ? "Approving…" : `Approve ${picked.size} selected`}
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4" data-testid="pending-search-bar">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="input-pending-search"
            placeholder="Search by name, phone, email…"
            className="sms-input pl-9"
          />
        </div>
        {search && (
          <button onClick={() => setSearch("")} data-testid="clear-pending-search" className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900">
            Clear
          </button>
        )}
      </div>

      {pendingNew > 0 && (
        <div
          data-testid="new-applications-banner"
          className="border border-[var(--sms-success)] bg-emerald-50 px-5 py-4 mb-6 flex items-center justify-between gap-3 sms-fade-up"
        >
          <div className="flex items-center gap-3">
            <span
              data-testid="new-applications-pulse-dot"
              className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--sms-success)] shadow-[0_0_0_4px_rgba(22,163,74,0.25)]"
            />
            <div>
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-emerald-700">
                Live · new submissions
              </div>
              <div className="text-sm text-slate-900 font-medium">
                {pendingNew} new application{pendingNew === 1 ? "" : "s"} arrived since you last loaded the queue.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              data-testid="btn-reload-pending"
              className="sms-btn-primary inline-flex items-center gap-2"
            >
              <ArrowsClockwise size={14} weight="bold" />
              Reload list
            </button>
            <button
              type="button"
              onClick={() => setPendingNew(0)}
              data-testid="btn-dismiss-new-banner"
              aria-label="Dismiss"
              className="text-slate-500 hover:text-slate-900 transition-colors p-2"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      <div className="border border-slate-200">
        <table className="w-full text-sm" data-testid="pending-table">
          <thead className="bg-slate-50">
            <tr className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-4 py-3 w-10">
                <button onClick={toggleAll} data-testid="bulk-toggle-all" className="text-slate-500 hover:text-slate-900">
                  {picked.size === items.length && items.length > 0 ? <CheckSquare size={16} weight="duotone" /> : <Square size={16} weight="bold" />}
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">Applicant</th>
              <th className="text-left px-4 py-3 font-medium">Course</th>
              <th className="text-left px-4 py-3 font-medium">Phone</th>
              <th className="text-left px-4 py-3 font-medium">Submitted</th>
              <th className="text-right px-4 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400 font-mono-sms text-xs uppercase tracking-[0.2em]">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-slate-400">No pending applications. The queue is clear.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} data-testid={`pending-row-${r.id}`} className="border-t border-slate-200 hover:bg-slate-50 sms-fade-up">
                <td className="px-4 py-4">
                  <button onClick={() => toggle(r.id)} data-testid={`bulk-pick-${r.id}`} className={picked.has(r.id) ? "text-[var(--sms-primary)]" : "text-slate-400 hover:text-slate-900"}>
                    {picked.has(r.id) ? <CheckSquare size={16} weight="duotone" /> : <Square size={16} weight="bold" />}
                  </button>
                </td>
                <td className="px-4 py-4">
                  <div className="font-medium">{r.full_name}</div>
                  <div className="text-xs text-slate-500">Age {r.age} · {r.gender}</div>
                </td>
                <td className="px-4 py-4 text-slate-700">{r.course_name || "—"}</td>
                <td className="px-4 py-4 font-mono-sms text-xs text-slate-700">{r.phone}</td>
                <td className="px-4 py-4 font-mono-sms text-xs text-slate-500">
                  {new Date(r.submitted_at).toLocaleString()}
                </td>
                <td className="px-4 py-4 text-right">
                  <button
                    onClick={() => setSelected(r)}
                    data-testid={`btn-review-${r.id}`}
                    className="sms-btn-secondary"
                  >
                    Review →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between" data-testid="pending-pagination">
          <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="prev-pending-page"
              className="sms-btn-secondary inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <div className="font-mono-sms text-xs tracking-[0.2em]" data-testid="pending-page-indicator">
              {page + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * PAGE_SIZE >= total}
              data-testid="next-pending-page"
              className="sms-btn-secondary inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {selected && (
        <RegistrationDialog
          registration={selected}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </AdminLayout>
  );
}
